export enum MembershipFrequency {
  DAILY = "DAILY",
  WEEKLY = "WEEKLY",
  BIWEEKLY = "BIWEEKLY",
  MONTHLY = "MONTHLY",
  EVERY_FOUR_WEEKS = "EVERY_FOUR_WEEKS",
  EVERY_FIVE_WEEKS = "EVERY_FIVE_WEEKS",
  EVERY_SIX_WEEKS = "EVERY_SIX_WEEKS",
  EVERY_TEN_WEEKS = "EVERY_TEN_WEEKS",
  EVERY_TWELVE_WEEKS = "EVERY_TWELVE_WEEKS",
  QUARTERLY = "QUARTERLY",
  EVERY_FOUR_MONTHS = "EVERY_FOUR_MONTHS",
  SEMI_ANNUALLY = "SEMI_ANNUALLY",
  YEARLY = "YEARLY",
}

export enum MemberStatus {
  ACTIVE = "ACTIVE",
  EXPIRED = "EXPIRED",
  CANCELLED = "CANCELLED",
  DELINQUENT = "DELINQUENT",
  FROZEN = "FROZEN",
  INACTIVE = "INACTIVE",
  PENDING = "PENDING",
  AWAITING_FORM = "AWAITING_FORM",
  SWITCHED = "SWITCHED",
}

// Mirrors TERMINAL_MEMBER_STATUSES in decoda/enums/membership.py: the member is done.
// Nothing bills it and no scheduled sweep picks it up again. Gate actions on this rather
// than listing statuses inline — INACTIVE was missed everywhere it was spelled out by hand.
export const TERMINAL_MEMBER_STATUSES: readonly MemberStatus[] = [
  MemberStatus.CANCELLED,
  MemberStatus.EXPIRED,
  MemberStatus.INACTIVE,
  MemberStatus.SWITCHED,
];

export function isTerminalMemberStatus(status: MemberStatus): boolean {
  return TERMINAL_MEMBER_STATUSES.includes(status);
}

export enum MembershipEnrollmentChargeMode {
  STAFF_CHOICE = "STAFF_CHOICE",
  DEFER_TODAY = "DEFER_TODAY",
}

export enum MembershipShopBillingStart {
  ENROLLMENT_DAY = "ENROLLMENT_DAY",
  FIRST_OF_MONTH = "FIRST_OF_MONTH",
  FIFTEENTH_OF_MONTH = "FIFTEENTH_OF_MONTH",
}

export enum MembershipDepositStrategy {
  MEMBERSHIP_ACCOUNT = "MEMBERSHIP_ACCOUNT",
  LOCATION_ACCOUNT = "LOCATION_ACCOUNT",
}

export enum MemberLogType {
  STATUS_CHANGE = "STATUS_CHANGE",
  MANUAL_PAYMENT = "MANUAL_PAYMENT",
  RENEWED = "RENEWED",
  PAYMENT_METHOD_CHANGE = "PAYMENT_METHOD_CHANGE",
  DISCOUNT_USED = "DISCOUNT_USED",
  DISCOUNT_RESTORED = "DISCOUNT_RESTORED",
  DISCOUNT_MODIFIED = "DISCOUNT_MODIFIED",
  ENROLLED = "ENROLLED",
  BILLING_DATE_CHANGE = "BILLING_DATE_CHANGE",
  SWITCHED = "SWITCHED",
  PRICE_CHANGE = "PRICE_CHANGE",
}

// What a plan's cycle billing gives the member. Mirrors MembershipBenefitType.
export enum MembershipBenefitType {
  PREPAYMENT_DISCOUNT = "PREPAYMENT_DISCOUNT",
  PATIENT_CREDIT_GRANT = "PATIENT_CREDIT_GRANT",
  NONE = "NONE",
}

// How a plan's cycle revenue is recognized. Mirrors MembershipRecognitionMode.
export enum MembershipRecognitionMode {
  PER_REDEMPTION = "PER_REDEMPTION",
  STRAIGHT_LINE = "STRAIGHT_LINE",
  CASH = "CASH",
  AS_CONSUMED = "AS_CONSUMED",
  SPLIT = "SPLIT",
}

// Which already-billed cycles a recognition change re-stamps. Mirrors MembershipRecognitionScope.
export enum MembershipRecognitionScope {
  FUTURE = "FUTURE",
  CURRENT = "CURRENT",
  ALL = "ALL",
}

// Mirrors VALID_MEMBERSHIP_RECOGNITION_COMBINATIONS; the API rejects any other pair.
export const VALID_RECOGNITION_MODES: Record<
  MembershipBenefitType,
  readonly MembershipRecognitionMode[]
> = {
  [MembershipBenefitType.PREPAYMENT_DISCOUNT]: [
    MembershipRecognitionMode.CASH,
    MembershipRecognitionMode.STRAIGHT_LINE,
    MembershipRecognitionMode.PER_REDEMPTION,
    MembershipRecognitionMode.SPLIT,
  ],
  [MembershipBenefitType.PATIENT_CREDIT_GRANT]: [
    MembershipRecognitionMode.CASH,
    MembershipRecognitionMode.STRAIGHT_LINE,
    MembershipRecognitionMode.AS_CONSUMED,
  ],
  [MembershipBenefitType.NONE]: [
    MembershipRecognitionMode.CASH,
    MembershipRecognitionMode.STRAIGHT_LINE,
  ],
};
